import { useEffect, useId, useCallback, type ReactNode, type ComponentType } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { RetryingImage } from '@/components/ui/RetryingImage'
import { useEscapeKey } from '@/hooks/ui/useEscapeKey'
import { useFocusTrap } from '@/hooks/ui/useFocusTrap'
import { useSwipeDismiss } from '@/hooks/ui/useSwipeDismiss'
import { PlusIcon, MinusIcon } from '@/icons'

/* ─── Main Shell ─── */

interface ActionSheetProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
}

function ActionSheet({ isOpen, onClose, children }: ActionSheetProps) {
  const titleId = useId()
  const focusTrapRef = useFocusTrap(isOpen)
  const { swipeRef, swipeHandlers, swipeStyle, backdropOpacity } = useSwipeDismiss({
    isActive: isOpen,
    onDismiss: onClose,
  })

  const { ref: swipeableRef, ...swipeProps } = swipeHandlers
  const composedRef = useCallback(
    (el: HTMLDivElement | null) => {
      ;(focusTrapRef as React.MutableRefObject<HTMLDivElement | null>).current = el
      ;(swipeRef as React.MutableRefObject<HTMLDivElement | null>).current = el
      swipeableRef(el)
    },
    [focusTrapRef, swipeRef, swipeableRef],
  )

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  useEscapeKey(onClose, isOpen)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/60 animate-fade-in"
        style={{ opacity: backdropOpacity }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={composedRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-lg bg-surface rounded-t-2xl animate-slide-up safe-area-bottom"
        style={swipeStyle}
        {...swipeProps}
      >
        <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
          <div className="w-10 h-1 bg-subtle-bg rounded-full" />
        </div>
        {children}
        <h2 id={titleId} className="sr-only">
          Actions
        </h2>
      </div>
    </div>
  )
}

/* ─── Preview ─── */

interface PreviewProps {
  image?: string | null
  imageClass?: string
  title: string
  subtitle?: string
  extra?: ReactNode
}

function Preview({ image, imageClass = 'w-12 h-16 object-cover rounded', title, subtitle, extra }: PreviewProps) {
  return (
    <div className="flex items-center gap-3 px-4 pb-3 border-b border-theme-border">
      {image && <RetryingImage src={image} alt={title} className={imageClass} />}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-primary truncate">{title}</h3>
        {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
        {extra}
      </div>
    </div>
  )
}

/* ─── Actions wrapper ─── */

function Actions({ children }: { children: ReactNode }) {
  return <div className="py-2">{children}</div>
}

/* ─── Action button ─── */

interface ActionProps {
  icon: ComponentType<{ className?: string }>
  iconColor: string
  label: string
  onClick: () => void
  disabled?: boolean
  highlighted?: boolean
}

function Action({ icon: Icon, iconColor, label, onClick, disabled, highlighted }: ActionProps) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      disabled={disabled}
      className={`w-full rounded-none justify-start gap-3 px-4 py-3 text-secondary hover:bg-hover active:bg-active ${highlighted ? 'bg-blue-600/20' : ''}`}
      icon={<Icon className={`w-5 h-5 ${iconColor}`} />}
    >
      {label}
    </Button>
  )
}

/* ─── Link action ─── */

interface LinkActionProps {
  to: string
  icon: ComponentType<{ className?: string }>
  label: string
  onClick?: () => void
}

function LinkAction({ to, icon: Icon, label, onClick }: LinkActionProps) {
  return (
    <Button
      as={Link}
      to={to}
      variant="ghost"
      onClick={onClick}
      className="w-full rounded-none justify-start gap-3 px-4 py-3 text-secondary hover:bg-hover active:bg-active"
      icon={<Icon className="w-5 h-5 text-muted" />}
    >
      {label}
    </Button>
  )
}

/* ─── Quantity stepper action ─── */

interface QuantityActionProps {
  icon: ComponentType<{ className?: string }>
  label: string
  quantity: number
  onIncrement: () => void
  onDecrement: () => void
  incrementDisabled?: boolean
  decrementDisabled?: boolean
}

function QuantityAction({
  icon: Icon,
  label,
  quantity,
  onIncrement,
  onDecrement,
  incrementDisabled,
  decrementDisabled,
}: QuantityActionProps) {
  return (
    <div className="flex items-center gap-2 px-4 py-2">
      <Icon className="w-5 h-5 text-blue-400 shrink-0" />
      <span className="text-secondary text-sm flex-1">{label}</span>
      <Button
        variant="ghost"
        size="icon"
        onClick={onDecrement}
        disabled={decrementDisabled}
        className="w-9 h-9 rounded-lg bg-well hover:bg-active text-red-400"
        aria-label={`Decrease ${label}`}
        icon={<MinusIcon className="w-4 h-4" />}
      />
      <span className="text-primary font-medium w-8 text-center" aria-live="polite">
        {quantity}
      </span>
      <Button
        variant="ghost"
        size="icon"
        onClick={onIncrement}
        disabled={incrementDisabled}
        className="w-9 h-9 rounded-lg bg-well hover:bg-active text-blue-400"
        aria-label={`Increase ${label}`}
        icon={<PlusIcon className="w-4 h-4" />}
      />
    </div>
  )
}

/* ─── Cancel button ─── */

function Cancel({ onClose }: { onClose: () => void }) {
  return (
    <div className="px-4 pb-4">
      <Button
        variant="secondary"
        onClick={onClose}
        className="w-full py-3 bg-well hover:bg-active active:bg-subtle-bg text-secondary"
      >
        Cancel
      </Button>
    </div>
  )
}

/* ─── Attach sub-components ─── */

ActionSheet.Preview = Preview
ActionSheet.Actions = Actions
ActionSheet.Action = Action
ActionSheet.QuantityAction = QuantityAction
ActionSheet.LinkAction = LinkAction
ActionSheet.Cancel = Cancel

export { ActionSheet }
